import { useState, useRef, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Header } from "@/components/Header";
import { CreditPackages } from "@/components/CreditPackages";
import { GARMENTS, COLORS, getGarment } from "@/data/garments";
import { CREDIT_COSTS } from "@/data/credits";
import { STAGE, downloadMockup, renderMockup, urlToDataURL, triggerDownload, watermarkDataURL } from "@/utils/exportMockup";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Download, Save, RotateCw, Maximize2, Trash2, Loader2, Sparkles, Scissors, Layers, Zap, Coins, AlignCenter, Crosshair, Check } from "lucide-react";

const Label = ({ children }) => (
  <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-medium">{children}</p>
);

const Cost = ({ n }) => (
  <span className="ml-auto inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-sm bg-white/15">
    <Coins className="w-3 h-3" /> {n}
  </span>
);

const SNAP = 6;

export default function Editor() {
  const { user, login, updateCredits } = useAuth();
  const location = useLocation();
  const [garmentKey, setGarmentKey] = useState("tshirt");
  const [view, setView] = useState("front");
  const [color, setColor] = useState("#FFFFFF");
  const [designs, setDesigns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [name, setName] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [bgBusy, setBgBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [guides, setGuides] = useState({ v: false, h: false });

  const fileRef = useRef(null);
  const drag = useRef(null);
  const designsRef = useRef([]);
  useEffect(() => { designsRef.current = designs; }, [designs]);

  const garment = getGarment(garmentKey);
  const garmentSrc = view === "front" ? garment.front : garment.back;
  const selected = designs.find((d) => d.id === selectedId) || null;

  // Reopen a saved mockup passed from the dashboard
  useEffect(() => {
    const m = location.state?.mockup;
    if (m) {
      setGarmentKey(m.garment);
      setColor(m.color);
      setView(m.view);
      setDesigns(m.designs || []);
      setName(m.name || "");
      toast(`Editando "${m.name}"`);
    }
  }, []); // eslint-disable-line

  const updateDesign = (id, patch) => setDesigns((ds) => ds.map((d) => (d.id === id ? { ...d, ...patch } : d)));

  const canSpend = (cost) => {
    if (!user) { toast("Inicia sesión para usar créditos"); login(); return false; }
    if ((user.credits ?? 0) < cost) { setBuyOpen(true); return false; }
    return true;
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = fileUrl(data.path);
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const aspect = img.naturalWidth / img.naturalHeight;
        const w = garment.area.w * STAGE;
        const h = w / aspect;
        const offset = designs.length * 24;
        const nd = { id: crypto.randomUUID(), imgSrc: url, aspect, w, h, x: garment.area.cx * STAGE - w / 2 + offset, y: garment.area.cy * STAGE - h / 2 + offset, rotation: 0 };
        setDesigns((ds) => [...ds, nd]);
        setSelectedId(nd.id);
        toast.success("Diseño añadido");
        setUploading(false);
      };
      img.onerror = () => { toast.error("No se pudo cargar la imagen"); setUploading(false); };
      img.src = url;
    } catch { toast.error("Error al subir la imagen"); setUploading(false); }
    e.target.value = "";
  };

  const onPointerDownDesign = (e, id) => {
    e.stopPropagation();
    setSelectedId(id);
    e.currentTarget.setPointerCapture(e.pointerId);
    const d = designsRef.current.find((x) => x.id === id);
    drag.current = { mode: "move", id, startX: e.clientX, startY: e.clientY, dx: d.x, dy: d.y };
  };

  const onPointerDownResize = (e, id) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const d = designsRef.current.find((x) => x.id === id);
    drag.current = { mode: "resize", id, startX: e.clientX, w: d.w };
  };

  const onPointerMove = useCallback((e) => {
    const dr = drag.current;
    if (!dr) return;
    const d = designsRef.current.find((x) => x.id === dr.id);
    if (!d) return;
    if (dr.mode === "move") {
      let nx = dr.dx + (e.clientX - dr.startX);
      let ny = dr.dy + (e.clientY - dr.startY);
      let v = false, h = false;
      if (Math.abs(nx + d.w / 2 - STAGE / 2) < SNAP) { nx = STAGE / 2 - d.w / 2; v = true; }
      if (Math.abs(ny + d.h / 2 - STAGE / 2) < SNAP) { ny = STAGE / 2 - d.h / 2; h = true; }
      setGuides({ v, h });
      setDesigns((ds) => ds.map((x) => (x.id === dr.id ? { ...x, x: nx, y: ny } : x)));
    } else if (dr.mode === "resize") {
      const w = Math.max(30, dr.w + (e.clientX - dr.startX));
      setDesigns((ds) => ds.map((x) => (x.id === dr.id ? { ...x, w, h: w / x.aspect } : x)));
    }
  }, []);

  const onPointerUp = useCallback(() => { drag.current = null; setGuides({ v: false, h: false }); }, []);

  useEffect(() => {
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => { window.removeEventListener("pointermove", onPointerMove); window.removeEventListener("pointerup", onPointerUp); };
  }, [onPointerMove, onPointerUp]);

  const removeDesign = (id) => { setDesigns((ds) => ds.filter((d) => d.id !== id)); if (selectedId === id) setSelectedId(null); };

  const centerDesign = () => {
    if (!selected) return;
    updateDesign(selected.id, { x: garment.area.cx * STAGE - selected.w / 2, y: garment.area.cy * STAGE - selected.h / 2 });
    toast.success("Diseño centrado");
  };

  const handleDownloadFree = async () => {
    try { await downloadMockup({ garmentSrc, color, designs }, `${name || garment.label}-mockup.png`); toast.success("PNG descargado"); }
    catch { toast.error("No se pudo exportar"); }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try { await confirm.run(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo completar"); }
    setConfirmBusy(false);
    setConfirm(null);
  };

  const handleSave = async () => {
    if (!canSpend(CREDIT_COSTS.mockup_normal)) return;
    const composite = await renderMockup({ garmentSrc, color, designs });
    const preview = await watermarkDataURL(composite);
    setConfirm({
      previewSrc: preview, cost: CREDIT_COSTS.mockup_normal, label: "Generar mockup",
      run: async () => {
        const { data } = await api.post("/mockups", { name: name || `${garment.label} ${new Date().toLocaleDateString("es-ES")}`, garment: garmentKey, color, view, designs, image_base64: composite });
        updateCredits(data.credits);
        toast.success("Mockup guardado (-1 crédito)");
      },
    });
  };

  const handleExport8k = async () => {
    if (!canSpend(CREDIT_COSTS.export_8k)) return;
    const composite = await renderMockup({ garmentSrc, color, designs });
    const preview = await watermarkDataURL(composite);
    setConfirm({
      previewSrc: preview, cost: CREDIT_COSTS.export_8k, label: "Exportar en 8K",
      run: async () => {
        const { data } = await api.post("/credits/consume", { action: "export_8k" });
        updateCredits(data.credits);
        const hi = await renderMockup({ garmentSrc, color, designs, outSize: 7680 });
        triggerDownload(hi, `${name || garment.label}-8K.png`);
        toast.success("Exportado en 8K (-3 créditos)");
      },
    });
  };

  const handleAdapt = async () => {
    if (!designs.length) return toast("Sube un diseño primero");
    if (!canSpend(CREDIT_COSTS.ai_adapt)) return;
    setAiBusy(true);
    try {
      const composite = await renderMockup({ garmentSrc, color, designs });
      const { data } = await api.post("/ai/adapt/preview", { image_base64: composite, garment: garmentKey, prompt: aiPrompt || null });
      setConfirm({
        previewSrc: data.preview, cost: data.cost, label: "Adaptar con IA",
        run: async () => {
          const r = await api.post("/ai/confirm", { preview_id: data.preview_id });
          updateCredits(r.data.credits);
          setAiResult(fileUrl(r.data.path));
          toast.success("Mockup con IA listo (-2 créditos)");
        },
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "La IA falló, inténtalo de nuevo"); }
    setAiBusy(false);
  };

  const handleRemoveBg = async () => {
    if (!selected) return toast("Selecciona un diseño");
    if (!canSpend(CREDIT_COSTS.remove_bg)) return;
    setBgBusy(true);
    try {
      const dataUrl = await urlToDataURL(selected.imgSrc);
      const { data } = await api.post("/ai/remove-bg/preview", { image_base64: dataUrl });
      const sel = selected;
      setConfirm({
        previewSrc: data.preview, cost: data.cost, label: "Eliminar fondo",
        run: async () => {
          const r = await api.post("/ai/confirm", { preview_id: data.preview_id });
          updateCredits(r.data.credits);
          const newUrl = fileUrl(r.data.path);
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => { const aspect = img.naturalWidth / img.naturalHeight; updateDesign(sel.id, { imgSrc: newUrl, aspect, h: sel.w / aspect }); };
          img.src = newUrl;
          toast.success("Fondo eliminado (-1 crédito)");
        },
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar el fondo"); }
    setBgBusy(false);
  };

  const centered = guides.v && guides.h;

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background text-foreground">
      <Header />
      <div className="flex-1 grid grid-cols-1 md:grid-cols-12 overflow-hidden">
        {/* LEFT PANEL */}
        <aside className="md:col-span-3 border-r border-border bg-white overflow-y-auto p-6 flex flex-col gap-8" data-testid="left-panel">
          <div className="flex flex-col gap-3">
            <Label>Prenda</Label>
            <div className="grid grid-cols-2 gap-2">
              {GARMENTS.map((g) => (
                <button key={g.key} data-testid={`garment-${g.key}`} onClick={() => setGarmentKey(g.key)}
                  className={`relative border rounded-sm p-2 flex flex-col items-center gap-1 transition-colors ${garmentKey === g.key ? "border-[#002FA7] bg-[#002FA7]/5" : "border-border hover:border-neutral-400"}`}>
                  <img src={g.front} alt={g.label} className="h-14 object-contain" />
                  <span className="text-[11px] uppercase tracking-wider text-neutral-600">{g.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Label>Vista</Label>
            <Tabs value={view} onValueChange={setView}>
              <TabsList className="w-full rounded-sm grid grid-cols-2">
                <TabsTrigger value="front" className="rounded-sm text-xs uppercase tracking-wider" data-testid="view-front">Frontal</TabsTrigger>
                <TabsTrigger value="back" className="rounded-sm text-xs uppercase tracking-wider" data-testid="view-back">Trasera</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="flex flex-col gap-3">
            <Label>Color de prenda</Label>
            <div className="grid grid-cols-5 gap-2">
              {COLORS.map((c) => (
                <button key={c.value} title={c.name} data-testid={`color-${c.value.replace("#", "")}`} onClick={() => setColor(c.value)}
                  className={`w-full aspect-square rounded-sm border transition-transform hover:scale-105 ${color === c.value ? "ring-2 ring-[#002FA7] ring-offset-1" : "border-neutral-300"}`}
                  style={{ backgroundColor: c.value }} />
              ))}
            </div>
            <label className="flex items-center gap-2 border border-border rounded-sm px-3 h-10 cursor-pointer hover:border-neutral-400">
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-6 h-6 rounded-sm border-0 bg-transparent cursor-pointer p-0" data-testid="custom-color-input" />
              <span className="text-xs uppercase tracking-wider text-neutral-500">Color personalizado</span>
              <span className="ml-auto text-xs tabular-nums text-neutral-400">{color.toUpperCase()}</span>
            </label>
          </div>
        </aside>

        {/* CANVAS */}
        <main className="md:col-span-6 bg-[#F4F4F5] relative flex items-center justify-center p-6 overflow-auto" data-testid="canvas-area" onPointerDown={() => setSelectedId(null)}>
          <div className="relative bg-white shadow-sm border border-border overflow-hidden shrink-0"
            style={{ width: STAGE, height: STAGE, backgroundImage: "radial-gradient(#e5e5e5 1px, transparent 1px)", backgroundSize: "18px 18px" }}
            data-testid="mockup-stage" onPointerDown={(e) => e.stopPropagation()}>
            <img src={garmentSrc} alt={garment.label} draggable={false} className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none" />
            <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: color, mixBlendMode: "multiply", WebkitMaskImage: `url(${garmentSrc})`, maskImage: `url(${garmentSrc})`, WebkitMaskSize: "contain", maskSize: "contain", WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat", WebkitMaskPosition: "center", maskPosition: "center" }} />

            {designs.map((d) => (
              <div key={d.id} onPointerDown={(e) => onPointerDownDesign(e, d.id)} className="absolute cursor-move"
                style={{ left: d.x, top: d.y, width: d.w, height: d.h, transform: `rotate(${d.rotation}deg)` }} data-testid={`design-layer-${d.id}`}>
                <img src={d.imgSrc} alt="design" draggable={false} className="w-full h-full object-contain select-none pointer-events-none" />
                <div className={`absolute inset-0 border border-dashed transition-colors ${selectedId === d.id ? "border-[#002FA7]" : "border-transparent hover:border-neutral-400"}`} />
                {selectedId === d.id && (
                  <div onPointerDown={(e) => onPointerDownResize(e, d.id)} className="absolute -right-2 -bottom-2 w-4 h-4 bg-[#002FA7] rounded-sm cursor-se-resize" data-testid="resize-handle" />
                )}
              </div>
            ))}

            {/* Centering guides */}
            {guides.v && <div className="absolute top-0 bottom-0 left-1/2 w-px bg-[#EC4899] -translate-x-1/2 pointer-events-none z-20" data-testid="guide-vertical" />}
            {guides.h && <div className="absolute left-0 right-0 top-1/2 h-px bg-[#EC4899] -translate-y-1/2 pointer-events-none z-20" data-testid="guide-horizontal" />}
            {centered && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 pointer-events-none flex items-center gap-1.5 bg-[#EC4899] text-white text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-sm" data-testid="centered-indicator">
                <Crosshair className="w-3 h-3" /> Centrado
              </div>
            )}
          </div>

          {!designs.length && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs uppercase tracking-[0.2em] text-neutral-400 pointer-events-none">
              Sube uno o varios diseños para empezar
            </div>
          )}
        </main>

        {/* RIGHT PANEL */}
        <aside className="md:col-span-3 border-l border-border bg-white overflow-y-auto p-6 flex flex-col gap-7" data-testid="right-panel">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2"><Layers className="w-3.5 h-3.5 text-neutral-500" /><Label>Diseños</Label></div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleUpload} data-testid="file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" className="rounded-sm gap-2 border-dashed h-11" data-testid="upload-design-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Añadir diseño
            </Button>
            {designs.length > 0 && (
              <div className="flex flex-col gap-1.5">
                {designs.map((d, i) => (
                  <div key={d.id} onClick={() => setSelectedId(d.id)}
                    className={`flex items-center gap-2 p-1.5 rounded-sm border cursor-pointer ${selectedId === d.id ? "border-[#002FA7] bg-[#002FA7]/5" : "border-border hover:bg-neutral-50"}`} data-testid={`layer-item-${i}`}>
                    <div className="w-8 h-8 bg-neutral-100 rounded-sm overflow-hidden flex items-center justify-center shrink-0"><img src={d.imgSrc} alt="" className="w-full h-full object-contain" /></div>
                    <span className="text-xs text-neutral-600 flex-1">Diseño {i + 1}</span>
                    <button onClick={(e) => { e.stopPropagation(); removeDesign(d.id); }} className="text-neutral-400 hover:text-red-600 p-1" data-testid={`remove-layer-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selected && (
            <div className="flex flex-col gap-5 border-t border-border pt-5">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-neutral-600"><Maximize2 className="w-3.5 h-3.5" /><Label>Tamaño</Label></div>
                <Slider value={[selected.w]} min={40} max={STAGE} step={1} onValueChange={([v]) => updateDesign(selected.id, { w: v, h: v / selected.aspect })} data-testid="scale-slider" />
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-neutral-600"><RotateCw className="w-3.5 h-3.5" /><Label>Rotación</Label></div>
                <Slider value={[selected.rotation]} min={-180} max={180} step={1} onValueChange={([v]) => updateDesign(selected.id, { rotation: v })} data-testid="rotation-slider" />
              </div>
              <Button onClick={centerDesign} variant="outline" className="rounded-sm gap-2" data-testid="center-btn">
                <AlignCenter className="w-4 h-4" /> Centrar en la prenda
              </Button>
              <Button onClick={handleRemoveBg} disabled={bgBusy} variant="outline" className="rounded-sm gap-2" data-testid="remove-bg-btn">
                {bgBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scissors className="w-4 h-4" />} Eliminar fondo (IA) <Cost n={CREDIT_COSTS.remove_bg} />
              </Button>
            </div>
          )}

          <div className="flex flex-col gap-3 border-t border-border pt-5">
            <div className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-[#002FA7]" /><Label>Inteligencia artificial</Label></div>
            <Input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="Estilo: modelo, fondo, iluminación…" className="rounded-sm text-sm" data-testid="ai-prompt-input" />
            <Button onClick={handleAdapt} disabled={aiBusy} className="rounded-sm gap-2 bg-[#002FA7] hover:bg-[#00248a]" data-testid="ai-adapt-btn">
              {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Adaptar prenda con IA <Cost n={CREDIT_COSTS.ai_adapt} />
            </Button>
          </div>

          <div className="flex flex-col gap-3 border-t border-border pt-5 mt-auto">
            <Label>Nombre del mockup</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Mi diseño" className="rounded-sm" data-testid="mockup-name-input" />
            <Button onClick={handleDownloadFree} variant="ghost" className="rounded-sm gap-2 justify-start text-neutral-600" data-testid="download-btn">
              <Download className="w-4 h-4" /> Descargar PNG (gratis)
            </Button>
            <Button onClick={handleExport8k} className="rounded-sm gap-2 bg-neutral-900 hover:bg-neutral-800" data-testid="export-8k-btn">
              <Zap className="w-4 h-4" /> Exportar en 8K <Cost n={CREDIT_COSTS.export_8k} />
            </Button>
            <Button onClick={handleSave} className="rounded-sm gap-2 bg-[#002FA7] hover:bg-[#00248a]" data-testid="save-btn">
              <Save className="w-4 h-4" /> Generar mockup <Cost n={CREDIT_COSTS.mockup_normal} />
            </Button>
          </div>
        </aside>
      </div>

      {/* Confirm (watermark preview) dialog */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && !confirmBusy && setConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Vista previa · {confirm?.label}</DialogTitle>
            <DialogDescription>Revisa el resultado. Los créditos solo se descuentan al confirmar.</DialogDescription>
          </DialogHeader>
          {confirm && <img src={confirm.previewSrc} alt="Vista previa" className="w-full rounded-sm border border-border" data-testid="confirm-preview" />}
          <div className="flex gap-2 mt-1">
            <Button variant="outline" className="rounded-sm flex-1" onClick={() => setConfirm(null)} disabled={confirmBusy} data-testid="confirm-cancel-btn">Cancelar</Button>
            <Button className="rounded-sm flex-1 gap-2 bg-[#002FA7] hover:bg-[#00248a]" onClick={runConfirm} disabled={confirmBusy} data-testid="confirm-accept-btn">
              {confirmBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Confirmar ({confirm?.cost} <Coins className="w-3 h-3" />)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI result dialog */}
      <Dialog open={!!aiResult} onOpenChange={(o) => !o && setAiResult(null)}>
        <DialogContent className="max-w-lg" data-testid="ai-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Mockup realista con IA</DialogTitle>
            <DialogDescription>Generado con IA a partir de tu diseño.</DialogDescription>
          </DialogHeader>
          {aiResult && <img src={aiResult} alt="Resultado IA" className="w-full rounded-sm border border-border" />}
          <a href={aiResult} download="mockup-ia.png" target="_blank" rel="noreferrer">
            <Button className="rounded-sm gap-2 w-full bg-[#002FA7] hover:bg-[#00248a]" data-testid="download-ai-btn"><Download className="w-4 h-4" /> Descargar imagen IA</Button>
          </a>
        </DialogContent>
      </Dialog>

      {/* Out of credits dialog */}
      <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
        <DialogContent className="max-w-2xl" data-testid="out-of-credits-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">Te has quedado sin créditos</DialogTitle>
            <DialogDescription>Compra un paquete para seguir generando mockups, usar IA y exportar en alta calidad.</DialogDescription>
          </DialogHeader>
          <CreditPackages compact />
        </DialogContent>
      </Dialog>
    </div>
  );
}
